import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/Header';
import CrisisOverlay from '../../components/CrisisOverlay';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Prepares text for Text-to-Speech by converting scripture references
 * to natural spoken format and cleaning up punctuation.
 * 
 * Examples:
 * - "John 3:16" → "John chapter 3 verse 16"
 * - "Jeremiah 29:11" → "Jeremiah chapter 29 verse 11"
 * - "Psalm 23:1-4" → "Psalm chapter 23 verses 1 through 4"
 */
const prepareTextForSpeech = (text, isSpanish = false) => {
  if (!text) return '';
  
  let result = text;
  
  // Convert Bible verse references to spoken format
  // Pattern: Book Chapter:Verse or Book Chapter:Verse-Verse
  // Examples: John 3:16, Jeremiah 29:11, Psalm 23:1-4, 1 Corinthians 13:4-7
  
  if (isSpanish) {
    // Spanish: "Juan 3:16" → "Juan capítulo 3 versículo 16"
    // With verse range: "Salmos 23:1-4" → "Salmos capítulo 23 versículos 1 al 4"
    result = result.replace(/(\d+):(\d+)-(\d+)/g, 'capítulo $1 versículos $2 al $3');
    result = result.replace(/(\d+):(\d+)/g, 'capítulo $1 versículo $2');
  } else {
    // English: "John 3:16" → "John chapter 3 verse 16"
    // With verse range: "Psalm 23:1-4" → "Psalm chapter 23 verses 1 through 4"
    result = result.replace(/(\d+):(\d+)-(\d+)/g, 'chapter $1 verses $2 through $3');
    result = result.replace(/(\d+):(\d+)/g, 'chapter $1 verse $2');
  }
  
  // Clean up other punctuation for natural speech
  result = result
    .replace(/;/g, ',')           // Semicolons to commas for natural pause
    .replace(/--/g, ', ')         // Double dashes to comma pause
    .replace(/\s+/g, ' ')         // Normalize whitespace
    .trim();
  
  return result;
};

const ReflectionResponse = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPremium, user } = useAuth();
  const { t, isSpanish, getTTSVoice } = useLanguage();
  const [showCrisisOverlay, setShowCrisisOverlay] = useState(false);
  const [speakingSection, setSpeakingSection] = useState(null); // Track which section is being read
  const [reflectionData, setReflectionData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Bottom 4 sections COLLAPSED by default to reduce scroll fatigue
  const [openSections, setOpenSections] = useState({
    fuel: false,
    footsteps: false,
    fieldnotes: false,
    soulanchor: false
  });
  
  // Simplified state for Copy button
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const data = location?.state?.reflectionData;
    if (!data) {
      navigate('/main-dashboard');
      return;
    }
    
    const responseData = data.response || data;
    const journalPrompts = responseData.field_notes || [];
    const userInputValue = data.user_input || data.userInput || '';
    
    // Include user_id from multiple sources - API response or auth context
    const userId = data.reflection_data?.user_id || data.user_id || responseData.user_id || user?.id;
    
    const newReflectionData = {
      id: data.id,
      user_id: userId,
      userInput: userInputValue,
      ...responseData,
      field_notes: Array.isArray(journalPrompts) ? journalPrompts : [],
      is_saved: true  // AUTO-SAVED: Always true now
    };
    
    console.log('[Init] Reflection loaded (auto-saved) with id:', data.id);
    
    setReflectionData(newReflectionData);
  }, [location, navigate, user]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  const handleDelete = async () => {
    if (!reflectionData?.id) return;
    
    setIsDeleting(true);
    try {
      await api.deleteReflection(reflectionData.id);
      navigate('/history', { replace: true });
    } catch (error) {
      console.error('Error deleting reflection:', error);
      alert(isSpanish ? 'Error al eliminar la reflexión' : 'Error deleting reflection');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // TTS - Read text aloud with STRICT PLAY/STOP TOGGLE
  // BILINGUAL: Uses premium voices for both English and Spanish
  // Uses prepareTextForSpeech to sanitize text for natural voice output
  // PC FIX: Added robust voice initialization and retry for desktop browsers
  const handleListen = useCallback((text, sectionId = 'all') => {
    if (!('speechSynthesis' in window)) {
      alert(isSpanish ? 'Tu navegador no soporta texto a voz' : 'Your browser does not support text-to-speech');
      return;
    }
    
    // STRICT TOGGLE: If ANY speech is playing, clicking ANY button STOPS it immediately
    if (window.speechSynthesis.speaking || speakingSection !== null) {
      window.speechSynthesis.cancel();
      setSpeakingSection(null);
      // If clicking the same section that was playing, just stop (don't restart)
      if (speakingSection === sectionId) {
        return;
      }
    }
    
    // Start new speech
    setSpeakingSection(sectionId);
    
    // CRITICAL: Sanitize text for natural TTS output (pass language for proper verse reading)
    const cleanedText = prepareTextForSpeech(text, isSpanish);
    
    // PC FIX: Function to actually speak with voice
    const speakWithVoice = () => {
      // Get voices and find the best one
      const voices = window.speechSynthesis.getVoices();
      const bestVoice = getTTSVoice(voices);
      
      const utterance = new SpeechSynthesisUtterance(cleanedText);
      
      // BILINGUAL VOICE SELECTION: Set premium voice for current language
      if (bestVoice) {
        utterance.voice = bestVoice;
        console.log(`[TTS] Using voice: ${bestVoice.name} (${bestVoice.lang})`);
      } else {
        console.warn('[TTS] No voice found, using default');
      }
      
      // Set language explicitly for better pronunciation
      utterance.lang = isSpanish ? 'es-ES' : 'en-US';
      
      // NATURAL SPEECH SETTINGS - Tuned for human-like delivery
      utterance.rate = 0.9;    // Slightly slower for natural cadence
      utterance.pitch = 1.0;   // Normal pitch
      utterance.volume = 1.0;  // Full volume
      
      utterance.onend = () => {
        console.log('[TTS] Finished speaking');
        setSpeakingSection(null);
      };
      
      utterance.onerror = (e) => {
        console.error('[TTS] Error:', e.error);
        setSpeakingSection(null);
        // PC FIX: Show user-friendly error
        if (e.error === 'not-allowed') {
          alert(isSpanish ? 'Por favor permite el audio en tu navegador' : 'Please allow audio in your browser');
        }
      };
      
      // PC FIX: Cancel any pending speech and ensure clean start
      window.speechSynthesis.cancel();
      
      // Small delay then speak
      setTimeout(() => {
        window.speechSynthesis.speak(utterance);
      }, 100);
    };
    
    // PC FIX: Chrome/Firefox sometimes need voices to be loaded first
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      // Voices not loaded yet - wait for them
      console.log('[TTS] Waiting for voices to load...');
      window.speechSynthesis.onvoiceschanged = () => {
        console.log('[TTS] Voices loaded, starting speech');
        speakWithVoice();
      };
      // Also try after a short delay as fallback
      setTimeout(() => {
        const retryVoices = window.speechSynthesis.getVoices();
        if (retryVoices.length > 0) {
          speakWithVoice();
        }
      }, 200);
    } else {
      // Voices already loaded
      speakWithVoice();
    }
  }, [speakingSection, getTTSVoice, isSpanish]);

  // Load voices on mount - CRITICAL for bilingual TTS support
  useEffect(() => {
    if ('speechSynthesis' in window) {
      // Initial voice load
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          console.log(`[TTS] ${voices.length} voices loaded`);
          // Log available voices for debugging
          const spanishVoices = voices.filter(v => v.lang && v.lang.startsWith('es'));
          const englishVoices = voices.filter(v => v.lang && v.lang.startsWith('en'));
          console.log(`[TTS] Spanish voices: ${spanishVoices.length}, English voices: ${englishVoices.length}`);
        }
      };
      
      loadVoices();
      // Chrome needs this event listener
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Listen Button Component - STRICT PLAY/STOP TOGGLE
  // Shows Play icon when stopped, Stop icon when playing
  const ListenButton = ({ text, sectionId, size = 'sm' }) => {
    const isPlaying = speakingSection === sectionId;
    return (
      <button
        onClick={() => handleListen(text, sectionId)}
        className={`flex items-center gap-2 text-sm transition-colors px-3 py-1.5 rounded-lg ${
          isPlaying 
            ? 'bg-error/20 text-error hover:bg-error/30' 
            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
        }`}
        data-testid={`listen-${sectionId}`}
        aria-label={isPlaying ? (isSpanish ? 'Detener' : 'Stop') : (isSpanish ? 'Escuchar' : 'Listen')}
      >
        <Icon name={isPlaying ? 'Square' : 'Volume2'} size={size === 'sm' ? 16 : 18} />
        {isPlaying ? t('stop') : t('listen')}
      </button>
    );
  };

  // ALWAYS VISIBLE Section
  const AlwaysVisibleSection = ({ title, icon, children, listenText, sectionId }) => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
            <Icon name={icon} size={20} color="#FFD700" />
          </div>
          <h3 className="font-heading font-semibold text-lg text-foreground">{title}</h3>
        </div>
        {listenText && <ListenButton text={listenText} sectionId={sectionId} />}
      </div>
      <div>{children}</div>
    </div>
  );

  // COLLAPSIBLE Section
  const CollapsibleSection = ({ id, title, icon, children, listenText }) => {
    const isOpen = openSections[id];
    return (
      <div className="mb-6 overflow-hidden">
        <button
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Icon name={icon} size={20} color="#FFD700" />
            </div>
            <h3 className="font-heading font-semibold text-lg text-foreground">{title}</h3>
          </div>
          <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={20} color="#94A3B8" />
        </button>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="mt-4 pt-4 border-t border-border">
                {listenText && (
                  <div className="mb-4">
                    <ListenButton text={listenText} sectionId={id} />
                  </div>
                )}
                {children}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // CLIPBOARD API - Copy Reflection to clipboard
  // 7-STEP CONTENT: Reflection → Scripture → Story → Fuel → Footsteps → Journaling → Soul Anchor
  const handleShare = async () => {
    // BUILD PLAIN TEXT BODY IN LOCKED 1-7 ORDER
    const nl = '\n';
    const dbl = '\n\n';
    const sep = '\n═══════════════════\n';
    
    // BILINGUAL LABELS
    const L = {
      reflection: isSpanish ? '💭 LA REFLEXIÓN' : '💭 THE REFLECTION',
      scripture: isSpanish ? '📖 VERSÍCULOS' : '📖 SCRIPTURE',
      story: isSpanish ? '📜 LA HISTORIA BÍBLICA' : '📜 THE BIBLE STORY',
      fuel: isSpanish ? '🔥 EL ÁNIMO' : '🔥 THE FUEL',
      footsteps: isSpanish ? '👣 LOS PASOS' : '👣 THE FOOTSTEPS',
      journaling: isSpanish ? '📝 ACTIVIDADES DE DIARIO' : '📝 JOURNALING ACTIVITIES',
      soulAnchor: isSpanish ? '⚓ ANCLA DEL ALMA' : '⚓ SOUL ANCHOR',
      noResponse: isSpanish ? '[Sin respuesta]' : '[No response]',
      footer: isSpanish ? 'Enviado desde Lumiveta' : 'Sent from Lumiveta'
    };

    // 1. THE REFLECTION (FIRST)
    const sec1 = reflectionData.reflection 
      ? `${L.reflection}${dbl}${reflectionData.reflection}` 
      : '';

    // 2. SCRIPTURE/VERSES
    const versesText = (reflectionData.verses || [])
      .map(v => `"${v?.text}"${nl}— ${v?.reference} (${v?.translation || 'WEB'})`)
      .join(dbl);
    const sec2 = versesText ? `${L.scripture}${dbl}${versesText}` : '';

    // 3. THE BIBLE STORY
    const storyText = reflectionData.story 
      ? `${reflectionData.story.title} (${reflectionData.story.reference})${dbl}${reflectionData.story.content}`
      : '';
    const sec3 = storyText ? `${L.story}${dbl}${storyText}` : '';

    // 4. THE FUEL
    const sec4 = reflectionData.fuel 
      ? `${L.fuel}${dbl}${reflectionData.fuel}` 
      : '';

    // 5. THE FOOTSTEPS
    const stepsText = (reflectionData.footsteps || [])
      .map((step) => `• ${step}`)
      .join(nl);
    const sec5 = stepsText ? `${L.footsteps}${dbl}${stepsText}` : '';

    // 6. JOURNALING - Just prompts (no user input boxes anymore)
    const prompts = reflectionData.field_notes || [];
    const journalText = prompts
      .map((prompt) => `• ${prompt}`)
      .join(dbl);
    const sec6 = journalText ? `${L.journaling}${dbl}${journalText}` : '';

    // 7. SOUL ANCHOR (LAST)
    const anchorText = Array.isArray(reflectionData.soul_anchor)
      ? reflectionData.soul_anchor.map((ex) => {
          if (typeof ex === 'object' && ex.title) {
            return `• ${ex.title}${nl}${ex.exercise}`;
          }
          return `• ${ex}`;
        }).join(dbl)
      : (reflectionData.soul_anchor || '');
    const sec7 = anchorText ? `${L.soulAnchor}${dbl}${anchorText}` : '';

    // ASSEMBLE BODY - Filter empty, join with separator
    const sections = [sec1, sec2, sec3, sec4, sec5, sec6, sec7].filter(Boolean);
    const footer = `---${nl}${L.footer}${nl}https://lumiveta.com`;
    const plainBody = sections.join(sep) + sep + footer;

    // CLIPBOARD API - High-performance copy with animated feedback
    try {
      await navigator.clipboard.writeText(plainBody);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Clipboard failed:', err);
      alert(isSpanish 
        ? 'Error al copiar. Intenta de nuevo.' 
        : 'Copy failed. Please try again.');
    }
  };

  if (!reflectionData) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">{isSpanish ? 'Cargando tu reflexión...' : 'Loading your reflection...'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isSpanish ? 'Tu Reflexión - Lumiveta' : 'Your Reflection - Lumiveta'}</title>
      </Helmet>

      <div className="min-h-dvh bg-background">
        <Header />
        
        {/* Added extra top padding for iPhone notch and bottom padding for action bar */}
        <main className="pt-28 sm:pt-24 pb-40 sm:pb-32 px-4" style={{ paddingTop: 'max(7rem, env(safe-area-inset-top) + 5rem)', paddingBottom: 'max(10rem, env(safe-area-inset-bottom) + 8rem)' }}>
          <div className="container mx-auto max-w-4xl">
            {/* Back Button */}
            <div className="mb-6">
              <Button variant="ghost" onClick={() => navigate('/main-dashboard')} data-testid="back-to-dashboard">
                <Icon name="ArrowLeft" size={18} className="mr-2" />
                {t('backToDashboard')}
              </Button>
            </div>

            {/* Title */}
            <div className="mb-8">
              <h1 className="font-heading font-bold text-3xl text-gold-gradient mb-2">{t('lightArrived')}</h1>
              <p className="text-muted-foreground">{t('scriptureGuidance')}</p>
            </div>

            {/* Crisis Warning */}
            {reflectionData?.is_crisis && (
              <div className="mb-6 p-4 glass border border-error/30 rounded-xl">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" size={24} color="#EF4444" />
                  <div>
                    <h3 className="font-semibold text-error mb-1">{t('crisisSupport')}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{t('crisisMessage')}</p>
                    <Button variant="destructive" size="sm" onClick={() => setShowCrisisOverlay(true)}>
                      {t('viewCrisisResources')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* User Input - Simple read-only display */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={20} color="#FFD700" />
                </div>
                <p className="text-sm text-muted-foreground">{isSpanish ? 'Compartiste:' : 'You shared:'}</p>
              </div>
              <p className="text-foreground whitespace-pre-wrap leading-relaxed">{reflectionData.userInput}</p>
            </div>

            {/* 7 SECTIONS */}
            <div className="space-y-6">
              
              {/* 1. REFLECTION - ALWAYS VISIBLE */}
              <AlwaysVisibleSection 
                title={t('reflection')} 
                icon="Heart" 
                listenText={reflectionData.reflection}
                sectionId="reflection"
              >
                <p className="text-foreground leading-relaxed whitespace-pre-line">{reflectionData.reflection}</p>
              </AlwaysVisibleSection>

              {/* 2. BIBLE VERSES - ALWAYS VISIBLE */}
              <AlwaysVisibleSection 
                title={t('theSeam')} 
                icon="BookOpen"
                listenText={(reflectionData.verses || [reflectionData.verse]).filter(Boolean).map(v => `${v?.text}. From ${v?.reference}, ${v?.translation || 'World English Bible'}.`).join(' ')}
                sectionId="verses"
              >
                <div className="space-y-6">
                  {(reflectionData.verses || (reflectionData.verse ? [reflectionData.verse] : [])).map((v, idx) => (
                    <div key={idx}>
                      <blockquote className="text-lg text-foreground italic mb-2">&ldquo;{v?.text}&rdquo;</blockquote>
                      <p className="text-sm text-primary font-medium">— {v?.reference} ({v?.translation || 'WEB'})</p>
                    </div>
                  ))}
                </div>
              </AlwaysVisibleSection>

              {/* 3. BIBLE STORY - ALWAYS VISIBLE */}
              <AlwaysVisibleSection 
                title={t('theMap')} 
                icon="Map" 
                listenText={`${reflectionData.story?.title}, from ${reflectionData.story?.reference}. ${reflectionData.story?.content}`}
                sectionId="story"
              >
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-4">
                    <h4 className="font-semibold text-foreground text-xl">{reflectionData.story?.title}</h4>
                    <span className="text-sm text-muted-foreground">({reflectionData.story?.reference})</span>
                  </div>
                  <p className="text-foreground leading-relaxed whitespace-pre-line">{reflectionData.story?.content}</p>
                </div>
              </AlwaysVisibleSection>

              {/* 4. FUEL - COLLAPSIBLE */}
              <CollapsibleSection id="fuel" title={t('fuel')} icon="Flame" listenText={reflectionData.fuel}>
                <p className="text-foreground leading-relaxed whitespace-pre-line">{reflectionData.fuel}</p>
              </CollapsibleSection>

              {/* 5. FOOTSTEPS - COLLAPSIBLE with Listen Button */}
              <CollapsibleSection 
                id="footsteps" 
                title={t('footsteps')} 
                icon="Footprints"
                listenText={reflectionData.footsteps?.join('. ')}
              >
                <div className="space-y-3">
                  {reflectionData.footsteps?.map((step, index) => {
                    const cleanStep = step?.replace(/\*/g, '') || '';
                    const firstSpaceIndex = cleanStep.indexOf(' ');
                    if (firstSpaceIndex > 0) {
                      const firstWord = cleanStep.substring(0, firstSpaceIndex);
                      const rest = cleanStep.substring(firstSpaceIndex);
                      return (
                        <p key={index} className="text-foreground leading-relaxed">
                          <span className="font-bold text-blue-400">{firstWord}</span>{rest}
                        </p>
                      );
                    }
                    return <p key={index} className="text-foreground leading-relaxed">{cleanStep}</p>;
                  })}
                </div>
              </CollapsibleSection>

              {/* 6. FIELD NOTES - COLLAPSIBLE with Listen button in section header */}
              <CollapsibleSection id="fieldnotes" title={t('fieldNotes')} icon="PenTool" listenText={reflectionData.field_notes?.join('. ')}>
                <p className="text-sm text-muted-foreground mb-4">
                  {isSpanish 
                    ? 'Toma tiempo para reflexionar sobre estas preguntas:'
                    : 'Take time to reflect on these questions:'}
                </p>
                <div className="space-y-3">
                  {reflectionData.field_notes?.map((prompt, index) => (
                    <p key={index} className="text-foreground leading-relaxed">{prompt}</p>
                  ))}
                </div>
              </CollapsibleSection>

              {/* 7. SOUL-ANCHOR (GROUNDING EXERCISES) - COLLAPSIBLE with Listen in section header */}
              <CollapsibleSection 
                id="soulanchor" 
                title={t('soulAnchor')} 
                icon="Anchor"
                listenText={Array.isArray(reflectionData.soul_anchor) 
                  ? reflectionData.soul_anchor.map(ex => typeof ex === 'object' ? `${ex.title}. ${ex.exercise}` : ex).join('. ') 
                  : reflectionData.soul_anchor}
              >
                <div className="space-y-4">
                  {Array.isArray(reflectionData.soul_anchor) ? (
                    reflectionData.soul_anchor.map((exercise, idx) => (
                      <div key={idx}>
                        {typeof exercise === 'object' && exercise.title ? (
                          <div>
                            <h4 className="font-semibold text-lg mb-2" style={{ color: '#D4AF37' }}>{exercise.title}</h4>
                            <p className="text-foreground leading-relaxed">{exercise.exercise}</p>
                          </div>
                        ) : (
                          <p className="text-foreground leading-relaxed">{exercise}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-foreground leading-relaxed">{reflectionData.soul_anchor}</p>
                  )}
                </div>
              </CollapsibleSection>

            </div>

            {/* Professional Help Notice */}
            <div className="mt-8 glass rounded-xl p-6 border border-border">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  <Icon name="Info" size={20} color="#94A3B8" />
                </div>
                <div>
                  <h3 className="font-heading font-medium text-foreground mb-2">
                    {isSpanish ? 'Recuerda: Hay Ayuda Profesional Disponible' : 'Remember: Professional Help is Available'}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {isSpanish 
                      ? 'Aunque la guía basada en la fe puede proporcionar consuelo y dirección, es importante buscar ayuda profesional cuando sea necesario.'
                      : "While faith-based guidance can provide comfort and direction, it's important to seek professional help when needed."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Fixed Action Bar - iPhone safe area support */}
        <div 
          className="fixed bottom-0 left-0 right-0 glass border-t border-border p-3 sm:p-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div className="container mx-auto max-w-4xl px-2">
            {/* Mobile: Stack vertically, Desktop: Side by side */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
              {/* Top row on mobile / Left side on desktop */}
              <div className="flex items-center justify-center sm:justify-start gap-2 order-2 sm:order-1">
                {/* LISTEN ALL BUTTON - Plays COMPLETE sequence in current language */}
                {/* Order: Reflection → Verses (with references) → Story → Fuel → Footsteps → Soul-Anchor */}
                <Button
                  variant={speakingSection === 'all' ? 'destructive' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    // BUILD COMPLETE TEXT - Include EVERYTHING with proper formatting for TTS
                    const sections = [];
                    
                    // 1. REFLECTION (Main response) - Start with this
                    if (reflectionData.reflection) {
                      sections.push(reflectionData.reflection);
                    }
                    
                    // 2. BIBLE VERSES - Include full text AND reference for each verse
                    if (reflectionData.verses && reflectionData.verses.length > 0) {
                      const versesText = reflectionData.verses.map(v => {
                        if (v?.text && v?.reference) {
                          return `${v.text}. From ${v.reference}.`;
                        }
                        return v?.text || '';
                      }).filter(Boolean).join(' ');
                      if (versesText) sections.push(versesText);
                    }
                    
                    // 3. BIBLE STORY - Title and content
                    if (reflectionData.story?.content) {
                      const storyText = reflectionData.story.title 
                        ? `${reflectionData.story.title}. ${reflectionData.story.content}`
                        : reflectionData.story.content;
                      sections.push(storyText);
                    }
                    
                    // 4. FUEL (Encouragement)
                    if (reflectionData.fuel) {
                      sections.push(reflectionData.fuel);
                    }
                    
                    // 5. FOOTSTEPS (Action Steps)
                    if (reflectionData.footsteps && reflectionData.footsteps.length > 0) {
                      const stepsText = reflectionData.footsteps.map((step, i) => `Step ${i+1}: ${step}`).join('. ');
                      sections.push(stepsText);
                    }
                    
                    // 6. SOUL-ANCHOR (Grounding Exercises)
                    if (reflectionData.soul_anchor) {
                      let anchorText = '';
                      if (Array.isArray(reflectionData.soul_anchor)) {
                        anchorText = reflectionData.soul_anchor.map((ex, i) => {
                          if (typeof ex === 'object' && ex.title && ex.exercise) {
                            return `Exercise ${i+1}: ${ex.title}. ${ex.exercise}`;
                          }
                          return typeof ex === 'string' ? `Exercise ${i+1}: ${ex}` : '';
                        }).filter(Boolean).join('. ');
                      } else {
                        anchorText = reflectionData.soul_anchor;
                      }
                      if (anchorText) sections.push(anchorText);
                    }
                    
                    // Join all sections with proper pauses (periods create natural pauses in TTS)
                    const allText = sections.filter(Boolean).join('. ');
                    console.log('[TTS Listen All] Playing complete reflection:', allText.substring(0, 200) + '...');
                    handleListen(allText, 'all');
                  }}
                  data-testid="listen-all-button"
                  aria-label={speakingSection === 'all' ? t('stop') : t('listenAll')}
                  className="flex-shrink-0"
                >
                  <Icon name={speakingSection === 'all' ? 'Square' : 'Volume2'} size={18} />
                  <span className="ml-1 text-xs sm:text-sm">{speakingSection === 'all' ? t('stop') : t('listenAll')}</span>
                </Button>
                
                {/* Delete button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-muted-foreground hover:text-error hover:bg-error/10 flex-shrink-0"
                  data-testid="delete-reflection-button"
                >
                  <Icon name="Trash2" size={18} />
                  <span className="ml-1 text-xs sm:text-sm">{isSpanish ? 'Eliminar' : 'Delete'}</span>
                </Button>
              </div>
              
              {/* Bottom row on mobile (main actions) / Right side on desktop */}
              <div className="flex items-center justify-center sm:justify-end gap-2 order-1 sm:order-2 w-full sm:w-auto">
                {/* Copy Reflection button - Clipboard API with animated feedback */}
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleShare}
                  data-testid="copy-reflection-button"
                  className={`flex-shrink-0 transition-all duration-300 gold-glow ${copied ? 'bg-success/20 border-success/50 text-success' : ''}`}
                >
                  <Icon name={copied ? "Check" : "Copy"} size={16} />
                  <span className="ml-1 text-xs sm:text-sm">
                    {copied 
                      ? (isSpanish ? '✓ Copiado' : '✓ Copied') 
                      : (isSpanish ? 'Copiar' : 'Copy')}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
              onClick={() => setShowDeleteConfirm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-xl"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-error/20 flex items-center justify-center">
                    <Icon name="AlertTriangle" size={24} color="#EF4444" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-foreground">
                      {isSpanish ? '¿Eliminar reflexión?' : 'Delete reflection?'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {isSpanish ? 'Esta acción no se puede deshacer.' : 'This action cannot be undone.'}
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                  >
                    {isSpanish ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button
                    variant="destructive"
                    fullWidth
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="bg-error hover:bg-error/90"
                  >
                    {isDeleting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        {isSpanish ? 'Eliminando...' : 'Deleting...'}
                      </>
                    ) : (
                      <>
                        <Icon name="Trash2" size={16} className="mr-2" />
                        {isSpanish ? 'Eliminar' : 'Delete'}
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <CrisisOverlay isVisible={showCrisisOverlay} onClose={() => setShowCrisisOverlay(false)} />
      </div>
    </>
  );
};

export default ReflectionResponse;
